import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import Count from '@risksmart-app/components/src/navigation/Count';
import { AppetiteStatus as Appetite_Status_Enum } from '@risksmart-app/domain/src/types/consts/appetite-status';
import { AppetiteType as Appetite_Type_Enum } from '@risksmart-app/domain/src/types/consts/appetite-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type {
  Acceptance_Bool_Exp,
  Appetite_Parent_Bool_Exp,
  Cause_Bool_Exp,
  Consequence_Bool_Exp,
  Control_Bool_Exp,
  Issue_Bool_Exp,
  Risk_Bool_Exp,
  Test_Result_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMessagesFeedStore } from 'src/components/messages/useMessagesFeedStore';

import {
  useGetAcceptanceCount,
  useGetAppetiteCount,
  useGetAssessmentActivityCount,
  useGetAssessmentCount,
  useGetAssessmentResultCount,
  useGetCauseCount,
  useGetConsequenceCount,
  useGetControlCount,
  useGetControlGroupCount,
  useGetImpactCount,
  useGetImpactRatingCount,
  useGetInternalAuditReportCount,
  useGetInternalAuditReportResultCount,
  useGetIssueCount,
  useGetOversightActivityCount,
  useGetOversightAssessmentCount,
  useGetOversightAssessmentResultCount,
  useGetRiskCount,
  useGetTestResultCount,
} from '@/hooks/queries';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

// NOTE (RSP-5727, corrected by COI-889, re-homed by COI-908): every badge count
// below is read through a `createQueryHook` count hook, and `createQueryHook`
// defaults `graphqlFetchPolicy` to `no-cache` — no count hook overrides it. An
// `evictFields` entry therefore cannot refresh a badge on either path; only
// `invalidateQueries` can. Each badge is also mounted for the whole session
// (`Navigation` sits outside the route outlet, and a collapsed section keeps its
// children mounted), so a missing invalidation is stale until a page reload.
//
// Since COI-908 the tRPC branch of every hook below reads ONE collapsed
// procedure (`frontend.navigation.counts`), so a mutation naming an individual
// `*Count` family no longer refreshes any badge — nothing observes those keys.
// `createMutationHook` now invalidates the nav-counts key after every successful
// mutation instead, which is why this list no longer has to be kept in sync with
// 45 mutation hooks by hand. Do NOT reintroduce per-badge invalidation here.
export const CountOptions = [
  'issue',
  'issueRiskEvent',
  'issueBreachLog',
  'issueConsumerDuty',
  'issueCustomerTrust',
  'issueGDPRBreachLog',
  'issuePCIBreachLog',
  'issueSARLog',
  'risk',
  'acceptance',
  'appetite',
  'notification',
  'impact',
  'impactRating',
  'assessment',
  'internalAudit',
  'internalAuditReport',
  'assessmentResult',
  'assessmentActivity',
  'internalAuditReportResult',
  'oversightAssessment',
  'oversightAssessmentResult',
  'oversightActivity',
  'consequence',
  'cause',
  'request',
  'control',
  'controlGroup',
  'testResult',
] as const;

export type TCountOptions = (typeof CountOptions)[number];

/**
 * Sums the per-table members of a rollup badge, preserving "no data" instead of
 * coercing it to 0.
 *
 * The three rollup badges add several table counts together. Since COI-908 a
 * member can be `undefined` because its count failed server-side (the collapsed
 * procedure nulls a failed count rather than throwing), and `Count` renders a
 * blank badge only for a non-number — so `?? 0` on every member would turn a
 * total outage into a confident "0". Any present member is still summed, which
 * matches the pre-collapse behaviour where a partial rollup showed the part it
 * had.
 */
const sumCounts = (counts: (number | undefined)[]): number | undefined =>
  counts.some((count) => typeof count === 'number')
    ? counts.reduce<number>((total, count) => total + (count ?? 0), 0)
    : undefined;

const ConnectedCount: FC<{
  isActive?: boolean;
  badge?: boolean;
  countName: TCountOptions;
}> = ({ isActive, badge, countName }) => {
  const { entityIds } = useEntityFilter();

  const issueCountWhere = useEntityWhereFilter<Issue_Bool_Exp>(
    Parent_Type_Enum.Issue,
    {
      Type: {
        _eq: 'issue',
      },
    }
  );
  // Every badge passes the SAME `entityIds` so all 19 hooks share one query key
  // and React Query dedupes them onto one request (COI-908). Which badges the
  // selection actually narrows is decided server-side: the main 'issue' badge is
  // entity-filtered, the subtype badges stay global. Sending `entityIds` here
  // does NOT filter them.
  const { data: issueData } = useGetIssueCount({
    queryArgs: { issueType: 'issue', entityIds, where: issueCountWhere },
    shouldSkip: countName !== 'issue',
  });
  const { data: issueRiskEventData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_risk_event',
      entityIds,
      where: { Type: { _eq: 'issue_risk_event' } },
    },
    shouldSkip: countName !== 'issueRiskEvent',
  });
  const { data: issueConsumerDutyData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_consumer_duty',
      entityIds,
      where: { Type: { _eq: 'issue_consumer_duty' } },
    },
    shouldSkip: countName !== 'issueConsumerDuty',
  });
  const { data: issueCustomerTrustData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_customer_trust',
      entityIds,
      where: { Type: { _eq: 'issue_customer_trust' } },
    },
    shouldSkip: countName !== 'issueCustomerTrust',
  });
  const { data: issueGDPRBreachLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_gdpr_breach_log',
      entityIds,
      where: { Type: { _eq: 'issue_gdpr_breach_log' } },
    },
    shouldSkip: countName !== 'issueGDPRBreachLog',
  });
  const { data: issuePCIBreachLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_pci_breach_log',
      entityIds,
      where: { Type: { _eq: 'issue_pci_breach_log' } },
    },
    shouldSkip: countName !== 'issuePCIBreachLog',
  });
  const { data: issueSARLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_sar_log',
      entityIds,
      where: { Type: { _eq: 'issue_sar_log' } },
    },
    shouldSkip: countName !== 'issueSARLog',
  });
  const { data: issueBreachLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_breach_log',
      entityIds,
      where: { Type: { _eq: 'issue_breach_log' } },
    },
    shouldSkip: countName !== 'issueBreachLog',
  });

  const consequenceCountWhere = useEntityWhereFilter<Consequence_Bool_Exp>(
    Parent_Type_Enum.Consequence
  );
  const { data: consequenceData } = useGetConsequenceCount({
    queryArgs: { entityIds, where: consequenceCountWhere },
    shouldSkip: countName !== 'consequence',
  });

  const causeCountWhere = useEntityWhereFilter<Cause_Bool_Exp>(
    Parent_Type_Enum.Cause
  );
  const { data: causeData } = useGetCauseCount({
    queryArgs: { entityIds, where: causeCountWhere },
    shouldSkip: countName !== 'cause',
  });

  const { data: impactData } = useGetImpactCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'impact',
  });
  const { data: impactRatingData } = useGetImpactRatingCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'impactRating',
  });

  const riskCountWhere = useEntityWhereFilter<Risk_Bool_Exp>(
    Parent_Type_Enum.Risk
  );
  const { data: riskData } = useGetRiskCount({
    queryArgs: { entityIds, where: riskCountWhere },
    shouldSkip: countName !== 'risk',
  });

  const appetiteCountWhere = useEntityWhereFilter<Appetite_Parent_Bool_Exp>(
    Parent_Type_Enum.Appetite,
    {
      Status: { _eq: Appetite_Status_Enum.Active },
      appetite: { AppetiteType: { _eq: Appetite_Type_Enum.Risk } },
    }
  );
  const { data: appetiteData } = useGetAppetiteCount({
    queryArgs: { entityIds, where: appetiteCountWhere },
    shouldSkip: countName !== 'appetite',
  });

  const acceptanceCountWhere = useEntityWhereFilter<Acceptance_Bool_Exp>(
    Parent_Type_Enum.Acceptance
  );
  const { data: acceptanceData } = useGetAcceptanceCount({
    queryArgs: { entityIds, where: acceptanceCountWhere },
    shouldSkip: countName !== 'acceptance',
  });
  const { data: assessmentData } = useGetAssessmentCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'assessment',
  });
  const { data: internalAuditReportData } = useGetInternalAuditReportCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'internalAuditReport',
  });
  const { data: oversightAssessmentData } = useGetOversightAssessmentCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'oversightAssessment',
  });
  const { data: assessmentResultData } = useGetAssessmentResultCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'assessmentResult',
  });

  const { data: assessmentActivityData } = useGetAssessmentActivityCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'assessmentActivity',
  });

  const { data: oversightActivityData } = useGetOversightActivityCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'oversightActivity',
  });

  const { data: internalAuditReportResultData } =
    useGetInternalAuditReportResultCount({
      queryArgs: { entityIds },
      shouldSkip: countName !== 'internalAuditReportResult',
    });

  const { data: oversightAssessmentResultData } =
    useGetOversightAssessmentResultCount({
      queryArgs: { entityIds },
      shouldSkip: countName !== 'oversightAssessmentResult',
    });

  const controlCountWhere = useEntityWhereFilter<Control_Bool_Exp>(
    Parent_Type_Enum.Control
  );
  const { data: controlData } = useGetControlCount({
    queryArgs: { entityIds, where: controlCountWhere },
    shouldSkip: countName !== 'control',
  });
  const { data: controlGroupData } = useGetControlGroupCount({
    queryArgs: { entityIds },
    shouldSkip: countName !== 'controlGroup',
  });

  const testResultCountWhere = useEntityWhereFilter<Test_Result_Bool_Exp>(
    Parent_Type_Enum.TestResult,
    { RatingType: { _in: ['assessment', 'rating'] } }
  );
  const { data: testResultData } = useGetTestResultCount({
    queryArgs: { entityIds, where: testResultCountWhere },
    shouldSkip: countName !== 'testResult',
  });
  // The server-side total, not the loaded items — a backlog past the first page
  // of 50 would otherwise under-count.
  const notificationsCount = useMessagesFeedStore(
    (state) => state.metadata.unread_count
  );

  let count: number | undefined = undefined;
  switch (countName) {
    case 'notification':
      count = notificationsCount;
      break;
    case 'issue':
      count = issueData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueBreachLog':
      count = issueBreachLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueConsumerDuty':
      count = issueConsumerDutyData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueCustomerTrust':
      count = issueCustomerTrustData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueGDPRBreachLog':
      count = issueGDPRBreachLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issuePCIBreachLog':
      count = issuePCIBreachLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueSARLog':
      count = issueSARLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueRiskEvent':
      count = issueRiskEventData?.issue_aggregate.aggregate?.count;
      break;
    case 'cause':
      count = causeData?.cause_aggregate.aggregate?.count;
      break;
    case 'consequence':
      count = consequenceData?.consequence_aggregate.aggregate?.count;
      break;
    case 'risk':
      count = riskData?.risk_aggregate.aggregate?.count;
      break;
    case 'acceptance':
      count = acceptanceData?.acceptance_aggregate?.aggregate?.count;
      break;
    case 'appetite':
      count = appetiteData?.appetite_parent_aggregate.aggregate?.count;
      break;
    case 'impact':
      count = impactData?.impact_aggregate.aggregate?.count;
      break;
    case 'impactRating':
      count = impactRatingData?.impact_rating_aggregate.aggregate?.count;
      break;
    case 'assessment':
      count = assessmentData?.assessment_aggregate.aggregate?.count;
      break;
    case 'internalAuditReport':
      count =
        internalAuditReportData?.internal_audit_report_aggregate.aggregate
          ?.count;
      break;
    case 'internalAuditReportResult':
      count = sumCounts([
        internalAuditReportResultData?.document_internal_audit_result_aggregate
          .aggregate?.count,
        internalAuditReportResultData
          ?.obligation_internal_audit_result_aggregate.aggregate?.count,
        internalAuditReportResultData
          ?.risk_controlled_internal_audit_result_aggregate.aggregate?.count,
        internalAuditReportResultData
          ?.risk_uncontrolled_internal_audit_result_aggregate.aggregate?.count,
      ]);
      break;
    case 'oversightAssessment':
      count =
        oversightAssessmentData?.oversight_assessment_aggregate.aggregate
          ?.count;
      break;
    case 'oversightAssessmentResult':
      count = sumCounts([
        oversightAssessmentResultData?.document_oversight_result_aggregate
          .aggregate?.count,
        oversightAssessmentResultData?.obligation_oversight_result_aggregate
          .aggregate?.count,
        oversightAssessmentResultData
          ?.risk_controlled_oversight_result_aggregate.aggregate?.count,
        oversightAssessmentResultData
          ?.risk_uncontrolled_oversight_result_aggregate.aggregate?.count,
      ]);
      break;
    case 'control':
      count = controlData?.control_aggregate.aggregate?.count;
      break;
    case 'controlGroup':
      count = controlGroupData?.control_group_aggregate.aggregate?.count;
      break;
    case 'testResult':
      count = testResultData?.test_result_aggregate.aggregate?.count;
      break;
    case 'assessmentResult':
      count = sumCounts([
        assessmentResultData?.document_assessment_result_aggregate.aggregate
          ?.count,
        assessmentResultData?.obligation_assessment_result_aggregate.aggregate
          ?.count,
        assessmentResultData?.risk_assessment_result_aggregate.aggregate?.count,
      ]);
      break;
    case 'assessmentActivity':
      count =
        assessmentActivityData?.assessment_activity_aggregate.aggregate?.count;
      break;
    case 'oversightActivity':
      count =
        oversightActivityData?.assessment_activity_aggregate.aggregate?.count;
      break;
  }

  return <Count count={count} badge={badge} isActive={isActive} />;
};

export default ConnectedCount;
